<template>
  <DetailInfo class="resume-info-container" :btnList="btnList" :actionBtnList="actionBtnList">
    <template #exportPDFBtn="{ btn, label }">
      <el-dropdown
        class="export-btn-dropdown"
        trigger="click"
        :disabled="btn.disabled"
        @command="handleExportBtnClick"
      >
        <el-button v-if="btn.tooltip" v-auto-tooltip.always="btn.tooltip" class="export-btn" :disabled="btn.disabled">
          <i class="btn-icon" :class="btn.iconClass"></i>
          <span class="btn-label">{{ label }}</span>
        </el-button>
        <el-button v-else class="export-btn" :disabled="btn.disabled">
          <i class="btn-icon" :class="btn.iconClass"></i>
          <span class="btn-label">{{ label }}</span>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu class="btn-dropdown-menu">
            <el-dropdown-item
              v-for="(item, index) in exportBtnList"
              :key="index"
              :command="item"
            >
              {{ item.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </template>
    <template #exportConsentInfoBtn="{ btn, label }">
      <i class="btn-icon" :class="btn.iconClass"></i>
      <span class="btn-label">{{ label }}</span>
    </template>
    <template #exportWordBtn="{ btn, label }">
      <i class="btn-icon" :class="btn.iconClass"></i>
      <span class="btn-label">{{ label }}</span>
    </template>
    <template #groupBtn="{ btn, label }">
      <el-popover v-model="group.showPopover" trigger="manual" popper-class="group-popover" :disabled="btn.disabled">
        <template #reference>
          <el-button v-if="btn.tooltip" v-auto-tooltip.always="btn.tooltip" :disabled="btn.disabled" class="head-btn" @click="onGroupBtnClick">{{ label }}</el-button>
          <el-button v-else :disabled="btn.disabled" class="head-btn" @click="onGroupBtnClick">{{ label }}</el-button>
        </template>
        <div v-rect-loading="group.loading" v-clickoutside="closeGroupListPopover" class="group-dropdown" :class="group.loading && 'loading'">
          <div class="dropdown-item operate-btn add-btn" @click="handleAddGroupBtnClick">{{ $t(`${i18nPrefix}.btn.addGroup`) }}</div>
          <div v-if="isInGroup" class="dropdown-item operate-btn move-btn" @click="handleMoveGroupBtnClick">{{ $t(`${i18nPrefix}.btn.moveGroup`) }}</div>
          <div
            v-for="(group, index) in group.application"
            class="dropdown-item group-info"
            :key="index"
            :class="group.selected && 'selected'"
            @click="handleGroupClick(group)"
          >
            <div class="dot"></div>
            <div class="group-name" :title="group.text">{{ group.text }}</div>
            <div class="count">({{ group.count }})</div>
          </div>
        </div>
      </el-popover>
    </template>
    <div v-show="viewResume.show" class="resume-viewer-container">
      <PDFViewer v-if="viewResume.pdf" ref="viewer" class="viewer" :fileURL="viewResume.fileURL" />
      <DocxViewer v-else-if="viewResume.docx" ref="viewer" class="viewer" :fileURL="viewResume.fileURL" />
      <HTMLViewer v-else-if="viewResume.html" ref="viewer" class="viewer" :fileURL="viewResume.fileURL" />
      <ImageViewer v-else-if="viewResume.image" ref="viewer" class="viewer" :fileURL="viewResume.fileURL" />
      <RectLoading v-show="viewResume.loading" class="viewer-loading"></RectLoading>
    </div>
    <div v-show="!viewResume.show" class="resume-info">
      <ResumeBaseInfo
        class="info-item"
        :isPotential="isPotential"
        :candidateInfo="candidateInfo"
        :candidateBaseInfo="candidateBaseInfo"
        :hiddenFieldList="resumeBaseInfoHiddenFieldList"
        @viewOriginResume="onViewResumeBtnClick"
      />
      <AIInterview
        v-if="aiAssessmentInfo && aiAssessmentInfo.has_ai_interview && aiAssessmentInfo.question_list"
        class="info-item"
        :candidateBaseInfo="candidateBaseInfo"
        :questionList="aiAssessmentInfo.question_list"
        :totalScore="aiAssessmentInfo.total_score"
        :pdfUrl="aiAssessmentInfo.pdf_url"
        :finishedTime="aiAssessmentInfo.finishedTime"
        :isDisabled="isDisabled"
        @update-finished-time="updateAiAssessmentInfo"
      />
      <Education
        v-if="candidateInfo.education_info.length > 0"
        class="info-item"
        :educations="candidateInfo.education_info"
      />
      <AdditionalInfo
        class="info-item"
        :languageList="candidateInfo.language_info"
      >
      </AdditionalInfo>
      <Consent
        class="info-item"
        v-if="candidateConsentInfo.consent_info?.length > 0"
        :candidateConsentInfo="candidateConsentInfo"
        :candidateBaseInfo="candidateBaseInfo"
        :isTalentPool="isTalentPool"
        :isPotential="isPotential"
        :isAgency="isAgency"
        :isDisabled="isDisabled"
      >
      </Consent>
    </div>
    <div v-show="!viewResume.show" class="resume-info">
      <AISummaryInfo
        v-if="showAiSummaryInfo"
        class="info-item ai-summary-info"
        :isTalentPool="isTalentPool"
        :loading="aiResumeMatchLoading"
        :titleI18nKey="`${i18nPrefix}.title.aiResumeMatchSummary`"
        :aiSummaryContent="!aiSummaryDialogInfo.isTypingEnabled ? aiSummaryDialogInfo.content : ''"
        :aiMatchSummaryContent="resumeAIMatchSummary"
        :fitScore="resumeAIFitScore"
        :fitDisplayScore="formatJobFitScore(resumeAIFitScore)"
        :fitDisplayColor="getJobFitScoreColor(resumeAIFitScore)"
        :candidateBaseInfo="candidateBaseInfo"
      ></AISummaryInfo>
      <WorkExperience
        v-if="candidateInfo.work_experience_info.length > 0"
        class="info-item work-experience"
        :dataList="candidateInfo.work_experience_info"
      >
      </WorkExperience>
      <WorkExperience
        v-if="candidateInfo.intern_experience_info.length > 0"
        isPartTime
        class="info-item part-time-experience"
        :dataList="candidateInfo.intern_experience_info"
      >
      </WorkExperience>
    </div>
    <InviteApplyJob
      ref="inviteApplyJobDialog"
      :jobId="jobId"
      :candidateId="candidateId"
    />
    <ContactCandidate
      ref="contactDialog"
      :jobId="jobId"
      :candidateIds="[candidateId]"
    />
    <CopyCandidateDialog
      ref="copyCandidateDialog"
      :isTalentPool="isTalentPool"
      :candidateList="[candidateId]"
      :applicationList="applicationList"
    ></CopyCandidateDialog>
    <AddGroupDialog
      ref="addGroupDialog"
      :hiddenCurrentGroup="isTalentPool"
      :hiddenTalentPoolGroup="isAgency"
      :groupList="group.unSelectedGroupList"
      :talentPoolGroupList="group.talentPool"
      @save="saveAddedGroup"
    />
    <MoveCandidateGroup
      ref="moveGroupDialog"
      :groupList="group.unSelectedGroupList"
      :selectedGroupList="group.selectedGroupList"
      @save="saveMovedGroup"
    />
    <AddComment
      v-if="!needHiddenActivity && !isAgency"
      ref="addCommentDialog"
      :jobId="jobId"
      :candidateId="candidateId"
      :isTalentPool="isTalentPool"
      :showInterviewerCanView="showInterviewerCanView"
    />
    <AISummary
      :show.sync="aiSummaryDialogInfo.show"
      :loading.sync="aiSummaryDialogInfo.loading"
      :content="aiSummaryDialogInfo.content"
      :isTypingEnabled="aiSummaryDialogInfo.isTypingEnabled"
      @inviteHMRate="handleInviteHMRate"
      @inviteHMInterview="handleInviteHMInterview"
      @save="handleSaveAIComments"
      @closeTyping="handleCloseTyping"
    ></AISummary>
    <InviteInterviewDialog
      ref="inviteDialog"
      :applicationList="applicationList"
      :saveWithEvent="inviteDialog.saveWithEvent"
      @sent="handleSentInterviewInvite"
      @save="handleSaveInterviewInvite"
    />
    <InviteHmRatingDialog
      ref="inviteHmRatingDialog"
      :applicationList="applicationList"
      @sent="handleSentHmRatingInvite"
    >
    </InviteHmRatingDialog>
    <ConfirmDialog
      class="confirm-send-dialog"
      :title="$t(confirmDialogInfo.title)"
      :content="$t(confirmDialogInfo.content)"
      :show.sync="confirmDialogInfo.show"
      :loading="confirmDialogInfo.loading"
      :cancelBtnLabelI18nKey="`${i18nPrefix}.btn.cancelSendInviteUpload`"
      :saveBtnLabelI18nKey="`${i18nPrefix}.btn.confirmSendInviteUpload`"
      @confirm="handleClickConfirm"
      @cancel="handleClickCancel"
    ></ConfirmDialog>
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { createI18nProperty, addI18nProperty, getI18nPropertyValue, isEn } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import Clickoutside from 'element-ui/src/utils/clickoutside'
import RectLoading from '@/components/loading/rectLoading.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import DetailInfo from './components/detailInfo.vue'
import ResumeBaseInfo from './components/resumeBaseInfo.vue'
import Education from './components/education.vue'
import AdditionalInfo from './components/additionalInfo.vue'
import Consent from './components/consent.vue'
import WorkExperience from './components/workExperience.vue'
import InviteApplyJob from './dialog/inviteApplyJob.vue'
import ContactCandidate from './dialog/contactCandidate.vue'
import CopyCandidateDialog from '../../applicantList/dialog/copyCandidateDialog.vue'
import AddGroupDialog from './dialog/addGroupDialog.vue'
import MoveCandidateGroup from './dialog/moveCandidateGroup.vue'
import AddComment from './dialog/addComment.vue'
import AIInterview from './components/aiInterview.vue'
import AISummary from './dialog/aiSummaryDialog.vue'
import AISummaryInfo from './components/aiSummaryInfo.vue'
import InviteInterviewDialog from '../../applicantList/dialog/inviteInterview.vue'
import InviteHmRatingDialog from '../../applicantList/dialog/inviteHmRating.vue'
import { AsyncTask, getJobFitScoreColor, formatJobFitScore } from '@/utils/common'
import { getFileExtension } from '@/utils/oss'
import applicationApi from '@/api/application'
import talentApi from '@/api/talent'
import agencyApi from '@/api/agency'

const AGENCY_UPLOADED_CANDIDATE_STATUS = {
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
  PENDING: 'PENDING'
}

export default {
  directives: {
    Clickoutside
  },
  components: {
    RectLoading,
    DetailInfo,
    ResumeBaseInfo,
    Education,
    AdditionalInfo,
    Consent,
    WorkExperience,
    InviteApplyJob,
    ContactCandidate,
    CopyCandidateDialog,
    AddGroupDialog,
    MoveCandidateGroup,
    AddComment,
    AIInterview,
    ConfirmDialog,
    AISummary,
    AISummaryInfo,
    InviteInterviewDialog,
    InviteHmRatingDialog,
    PDFViewer: () => import('@/components/resumeViewer/pdf.vue'),
    DocxViewer: () => import('@/components/resumeViewer/docx.vue'),
    HTMLViewer: () => import('@/components/resumeViewer/html.vue'),
    ImageViewer: () => import('@/components/resumeViewer/image.vue')
  },
  props: {
    aiResumeMatchLoading: {
      type: Boolean,
      default: false
    },
    candidateBaseInfo: {
      required: true
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isPotential: {
      type: Boolean,
      default: false
    },
    showInterviewerCanView: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    jobPremiumList: {
      type: Array,
      required: false
    },
    statusList: {
      type: Array,
      required: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const invitation = this.candidateBaseInfo.invitation_history?.mail_profile_note
    const personality = this.candidateBaseInfo.invitation_history?.mail_personality_note
    const problemSolving = this.candidateBaseInfo.invitation_history.mail_problem_solving_note
    const inviteDate = invitation?.send_time
    const invitationUploadResume = this.candidateBaseInfo.invitation_history?.invite_upload_resume
    const inviteUploadResumeDate = invitationUploadResume ? moment(invitationUploadResume.send_time).format('YYYY/MM/DD') : ''
    const invitationAIInterview = this.candidateBaseInfo.invitation_history?.invite_ai_interview
    const inviteAIInterviewDate = invitationAIInterview ? moment(invitationAIInterview.send_time).format('YYYY/MM/DD') : ''
    return {
      i18nPrefix: 'admin.application.candidate',
      getJobFitScoreColor,
      formatJobFitScore,
      invitationObj: {
        personality: {
          invited: Boolean(personality),
          inviteDate: moment(personality?.send_time).format('YYYY/MM/DD'),
          finished: this.candidateBaseInfo.personality_finished,
          typeId: 1
        },
        problemSolving: {
          invited: Boolean(problemSolving),
          inviteDate: moment(problemSolving?.send_time).format('YYYY/MM/DD'),
          finished: this.candidateBaseInfo.problem_solving_finished,
          typeId: 2
        }
      },
      finishProfileInvitation: {
        invited: Boolean(invitation),
        inviteDate: moment(inviteDate).format('YYYY/MM/DD'),
        hidden: false
      },
      inviteUploadResume: {
        invited: Boolean(invitationUploadResume),
        inviteDate: inviteUploadResumeDate
      },
      inviteAIInterview: {
        invited: Boolean(invitationAIInterview),
        inviteDate: inviteAIInterviewDate
      },
      candidateInfo: {
        base_info: {},
        work_experience_info: [],
        intern_experience_info: [],
        education_info: [],
        language_info: [],
        additional_info: {
          awards: null,
          hobbies: null,
          professional_member: null
        },
        group: [],
        group_history: []
      },
      shearLinkLoading: false,
      exportPdfStatus: {
        loading: false,
        task: null,
        finished: false
      },
      exportConsentStatus: {
        loading: false,
        task: null,
        finished: false
      },
      exportWordStatus: {
        loading: false,
        task: null,
        finished: false
      },
      group: {
        vid: 0,
        showPopover: false,
        loading: false,
        loaded: false,
        application: [],
        selectedGroupList: [],
        unSelectedGroupList: [],
        talentPool: []
      },
      candidateConsentInfo: {
        consent_info: []
      },
      viewResume: {
        show: false,
        type: null,
        get pdf () { return this.type === 'pdf' },
        get docx () { return this.type === 'docx' },
        get html () { return this.type === 'html' },
        get image () { return this.type === 'image' },
        get doc () { return this.type === 'doc' },
        hasFile: false,
        fileURL: null,
        loading: false
      },
      aiAssessmentInfo: null,
      confirmDialogInfo: {
        show: false,
        loading: false,
        title: null,
        content: null
      },
      createAISummaryStatus: {
        loading: false,
        task: null,
        finished: false
      },
      aiSummaryDialogInfo: {
        content: '',
        show: false,
        loading: false,
        isTypingEnabled: true
      },
      inviteDialog: {
        mode: 'CREATE',
        saveWithEvent: false,
        originInvitation: null,
        invitation: null
      }
    }
  },
  computed: {
    needHiddenActivity () {
      if (this.isPotential) {
        return !this.isTalentPool
      }
      return false
    },
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      if (this.isTalentPool || this.isPotential) {
        return null
      } else {
        return this.candidateBaseInfo.job.id
      }
    },
    statusKeyList () {
      return _.map(this.statusList, 'key')
    },
    resumeBaseInfoHiddenFieldList () {
      let hiddenList = []
      if (this.isTalentPool) {
        hiddenList = ['applyTime', 'sourceChannel', 'jobApplicationPlat', 'onboardDate', 'hiredDate', 'idNumber', 'referralInfo', 'referralChain', 'resumePath']
      } else {
        if (!this.statusKeyList.includes('Hired')) {
          hiddenList.push('onboardDate')
        }
        // 6cc1127e-62d2-11ec-930c-00163e0fbcc2 是 Ready to Hire
        // d71f06b8-4a32-11ef-94be-00163e0fbcc2 是 Ready for Hire
        if (!this.statusKeyList.includes('6cc1127e-62d2-11ec-930c-00163e0fbcc2') && !this.statusKeyList.includes('d71f06b8-4a32-11ef-94be-00163e0fbcc2')) {
          hiddenList.push('hiredDate')
        }
      }
      return hiddenList
    },
    accountHasEmail () {
      return Boolean(this.candidateBaseInfo.email)
    },
    hasExportConsentPermission () {
      if (!this.isTalentPool && !this.isPotential && !this.isAgency && this.candidateBaseInfo.company.root_company_id === 8223) {
        return true
      } else {
        return false
      }
    },
    hasExportWordPermission () {
      if (!this.isTalentPool && !this.isPotential && !this.isAgency && this.hasAnyPermission('COMPUTED_COMPANY_CAN_EXPORT_CANDIDATE_INFO_WORD')) {
        return true
      } else {
        return false
      }
    },
    isInGroup () {
      return (this.candidateInfo.group && this.candidateInfo.group.length > 0) || _.findIndex(this.group.application, 'selected') > -1
    },
    isPendingAgencyUploadedCandidate () {
      return this.candidateBaseInfo.agency_uploaded_candidate_status === AGENCY_UPLOADED_CANDIDATE_STATUS.PENDING
    },
    isRejectedAgencyUploadedCandidate () {
      return this.candidateBaseInfo.agency_uploaded_candidate_status === AGENCY_UPLOADED_CANDIDATE_STATUS.REJECTED
    },
    showExportReportBtn () {
      return this.candidateBaseInfo.can_view_report && this.hasAnyPermission('PREMIUM_ASSESSMENTS') && !this.hasAnyPermission('COMPANY_SURVEY_TYPE_B')
    },
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS') || _.includes(this.jobPremiumList, 'PREMIUM_ASSESSMENTS')
    },
    hasAISummaryPermission () {
      return this.hasAnyPermission('PREMIUM_AI_SUMMARY')
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    canViewAIFitScore () {
      return this.isAgency || this.hasAIResumeMatchPermission
    },
    exportBtnList () {
      const i18nPrefix = this.i18nPrefix
      const list = []
      if (this.candidateBaseInfo.resume_path) {
        list.push({
          label: this.$t(`${i18nPrefix}.btn.exportOriginResume`),
          onclick: this.handleExportOriginResume
        })
      }
      list.push({
        label: this.$t(`${i18nPrefix}.btn.exportAjingaProfilePDF`),
        onclick: this.handleExportResume
      })
      if (this.showExportBtnDropdown) {
        list.push({
          label: this.$t(`${i18nPrefix}.btn.report`),
          onclick: this.handleExportReport
        }, {
          label: this.$t(`${i18nPrefix}.btn.resumeAndReport`),
          onclick: this.handleExportResumeAndReport
        })
      }
      return list
    },
    btnList () {
      const vm = this
      const i18nPrefix = this.i18nPrefix
      let list
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid) {
        list = [
          {
            get label () {
              if (vm.isRejectedAgencyUploadedCandidate) {
                return vm.$t(`${i18nPrefix}.btn.agencyUploadedCandidate.rejected`)
              } else {
                return vm.$t(`${i18nPrefix}.btn.agencyUploadedCandidate.reject`)
              }
            },
            onclick: () => this.acceptAgencyUploadedCandidate(false)
          },
          {
            label: this.$t(`${i18nPrefix}.btn.agencyUploadedCandidate.accept`),
            onclick: () => this.acceptAgencyUploadedCandidate(true)
          }
        ]
        if (!this.isPendingAgencyUploadedCandidate && !this.isRejectedAgencyUploadedCandidate) {
          list = []
        }
      } else {
        list = [
          {
            get hidden () {
              return !vm.viewResume.hasFile
            },
            get label () {
              return vm.$t(`${i18nPrefix}.btn.${vm.viewResume.show ? 'viewProfile' : 'viewResume'}`)
            },
            onclick: this.onViewResumeBtnClick
          },
          {
            label: this.$t(`${i18nPrefix}.btn.editResume`),
            onclick: this.onEditResumeBtnClick
          },
          {
            label: this.$t(`${i18nPrefix}.btn.copyCandidate`),
            onclick: this.openCopyCandidateDialog,
            hidden: this.isAgency
          },
          {
            label: this.$t(`${i18nPrefix}.btn.share`),
            get loading () { return vm.shearLinkLoading },
            onclick: this.onShareBtnClick,
            hidden: this.isAgency
          },
          {
            slotName: 'groupBtn',
            label: this.$t(`${i18nPrefix}.btn.group`),
            hidden: this.isAgency
          },
          {
            slotName: 'exportPDFBtn',
            get loading () {
              const exportPdfStatus = vm.exportPdfStatus
              return exportPdfStatus?.task && !exportPdfStatus.finished
            },
            label: this.$t('common.btn.export'),
            get iconClass () {
              return vm.getExportIconClass(vm.exportPdfStatus)
            },
            hidden: this.isAgency
          },
          {
            hidden: !this.hasExportConsentPermission || this.isAgency,
            labelSlotName: 'exportConsentInfoBtn',
            get loading () {
              const exportConsentStatus = vm.exportConsentStatus
              return exportConsentStatus?.task && !exportConsentStatus.finished
            },
            label: this.$t(`${i18nPrefix}.btn.exportConsentPDF`),
            get iconClass () {
              return vm.getExportIconClass(vm.exportConsentStatus)
            },
            onclick: this.onExportConsentInfoPDF
          },
          {
            hidden: !this.hasExportWordPermission || this.isAgency,
            labelSlotName: 'exportWordBtn',
            get loading () {
              const exportWordStatus = vm.exportWordStatus
              return exportWordStatus?.task && !exportWordStatus.finished
            },
            label: this.$t(`${i18nPrefix}.btn.exportWord`),
            get iconClass () {
              return vm.getExportIconClass(vm.exportWordStatus)
            },
            onclick: this.onExportWord
          },
          {
            label: this.$t(`${i18nPrefix}.btn.contact`),
            onclick: this.onContactBtnClick,
            hidden: this.isAgency
          },
          // {
          //   label: this.$t(`${i18nPrefix}.btn.editOpenQuestion`),
          //   onclick: this.onEditOpenQuestion,
          //   hidden: this.isTalentPool || this.isAgency || !this.canOpenQuestionScreenType
          // },
          {
            label: this.$t(`${i18nPrefix}.btn.addComment`),
            onclick: this.onAddCommentBtnClick,
            hidden: this.needHiddenActivity || this.isAgency
          },
          {
            get label () {
              const invitation = vm.inviteAIInterview
              if (invitation.invited) {
                return vm.$t(`${i18nPrefix}.btn.inviteAiInterviewResent`, { date: invitation.inviteDate })
              }
              return vm.$t(`${i18nPrefix}.btn.inviteAiInterview`)
            },
            onclick: this.onSendAiInterviewBtnClick,
            hidden: this.isTalentPool || this.isAgency || !this.aiAssessmentInfo?.can_invite_ai_interview || !this.aiAssessmentInfo?.has_ai_interview
          }
        ]
      }
      _.forEach(list, (item) => {
        if (_.has(item, 'disabled')) {
          return
        }
        Object.defineProperty(item, 'disabled', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled
          }
        })
        Object.defineProperty(item, 'tooltip', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled && vm.$t(`${i18nPrefix}.message.disabledTip`)
          }
        })
      })
      return list
    },
    actionBtnList () {
      const vm = this
      const i18nPrefix = this.i18nPrefix
      const list = [
        {
          hidden: !this.hasAISummaryPermission || this.isTalentPool || this.isAgency || this.isPotential,
          get label () {
            // 如果已经生成过 AI Summary
            if (vm.aiSummaryDialogInfo.content) {
              return vm.$t(`${i18nPrefix}.btn.viewAISummary`)
            } else {
              return vm.$t(`${i18nPrefix}.btn.createAISummary`)
            }
          },
          get iconClass () {
            return vm.getCreateAISummaryIconClass(vm.createAISummaryStatus)
          },
          onclick: this.onCreateAISummaryBtnClick
        },
        {
          // 如果已经存在生成记录, 则不再显示生成按钮
          hidden: !this.hasAIResumeMatchPermission || vm.candidateBaseInfo.has_ai_match_record || this.isTalentPool || this.isAgency || this.isPotential,
          get label () {
            return vm.$t(`${i18nPrefix}.btn.createAIResumeMatch`)
          },
          iconClass: 'iconfont icona-ResumeSummary',
          loading: this.aiResumeMatchLoading,
          onclick: this.onCreateAIResumeMatchBtnClick
        }
      ]
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid) {

      } else {
        list.push(
          {
            label: this.$t(`${i18nPrefix}.btn.inviteUploadResume`),
            onclick: this.handleInviteCandidateUploadResume,
            hidden: this.isAgency || this.isTalentPool
          },
          {
            label: this.$t(`${i18nPrefix}.btn.inviteApplyJob`),
            onclick: this.openInviteApplyJobDialog,
            hidden: this.isAgency
          },
          {
            hidden: this.finishProfileInvitation?.finished || !this.accountHasEmail || this.isAgency,
            get label () {
              const invitation = vm.finishProfileInvitation
              if (invitation?.invited) {
                if (invitation.finished) {
                  return vm.$t(`${i18nPrefix}.btn.invited`, { date: invitation.inviteDate })
                } else {
                  return vm.$t(`${i18nPrefix}.btn.invitedAndResent`, { date: invitation.inviteDate })
                }
              } else {
                return vm.$t(`${i18nPrefix}.btn.inviteFinishProfile`)
              }
            },
            onclick: this.inviteFinishProfile
          }
        )
      }
      list.push(
        {
          hidden: this.isTalentPool || !this.hasAnyPermission('PERM_DELETE_CANDIDATES_APPLICATION') || !this.hasAnyPermission('COMPUTED_COMPANY_HAS_POLICY'),
          label: this.$t(`${i18nPrefix}.btn.revokeApplication`),
          get disabled () { return vm.candidateBaseInfo.is_revoked },
          confirm: true,
          confirmTitle: this.$t(`${i18nPrefix}.message.revokeApplicationConfirmTip`),
          confirmBtnLabel: this.$t('common.label.yes'),
          cancelBtnLabel: this.$t('common.label.no'),
          onconfirm: this.onRevokeApplicationBtnClick
        },
        {
          hidden: !this.hasAnyPermission('PERM_DELETE_CANDIDATES_APPLICATION') || !this.hasAnyPermission('COMPUTED_COMPANY_HAS_POLICY'),
          label: this.$t(`${i18nPrefix}.btn.revokeAllApplication`),
          get disabled () { return vm.candidateBaseInfo.is_revoked },
          confirm: true,
          confirmTitle: this.$t(`${i18nPrefix}.message.revokeAllApplicationConfirmTip`),
          confirmBtnLabel: this.$t('common.label.yes'),
          cancelBtnLabel: this.$t('common.label.no'),
          onconfirm: this.onRevokeAllApplicationBtnClick
        }
      )
      _.forEach(list, (item) => {
        Object.defineProperty(item, 'disabled', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled
          }
        })
        Object.defineProperty(item, 'tooltip', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled && vm.$t(`${i18nPrefix}.message.disabledTip`)
          }
        })
      })
      return list
    },
    canOpenQuestionScreenType () {
      if (this.isTalentPool) {
        return
      }
      // 前置问卷 , 调查问卷, 招聘人员筛选 按照这个顺序有则直接返回问卷类型
      const hasPrescreenQuestion = this.candidateBaseInfo.job.has_prescreen_question
      const hasStandardQuestion = this.candidateBaseInfo.job.has_standard_question
      const hasHrQuestion = this.candidateBaseInfo.job.has_hr_question
      if (hasPrescreenQuestion) {
        return 'PRESCREEN_QUESTIONNAIRE'
      } else if (hasStandardQuestion) {
        return 'JOB_QUESTIONNAIRE'
      } else if (hasHrQuestion) {
        return 'HR_QUESTIONNAIRE'
      } else {
        return null
      }
    },
    applicationList () {
      return [{
        job: {
          ...this.candidateBaseInfo.job,
          company: this.candidateBaseInfo.company
        },
        candidate: {
          id: this.candidateId,
          name: this.candidateBaseInfo.other_name,
          ai_summary_info: this.candidateInfo.ai_summary_info
        }
      }]
    },
    resumeAIMatchSummary () {
      if (!this.canViewAIFitScore) {
        return ''
      }
      return getI18nPropertyValue(this.candidateBaseInfo, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    },
    resumeAIFitScore () {
      return this.canViewAIFitScore ? this.candidateBaseInfo.resume_ai_match_score : null
    },
    hasSimilarCandidate () {
      return this.hasAnyPermission(['PERM_VIEW_SIMILAR_CANDIDATE', 'PREMIUM_SIMILAR_CANDIDATE'])
    },
    showAiSummaryInfo () {
      if (this.aiResumeMatchLoading) {
        return true
      } else if (!this.isAgency && !this.isPotential) {
        if (this.isTalentPool) {
          return this.hasSimilarCandidate
        } else {
          if (this.hasSimilarCandidate) {
            return true
          } else if (this.resumeAIMatchSummary) {
            return true
          } else if (this.aiSummaryDialogInfo.content && !this.aiSummaryDialogInfo.isTypingEnabled) {
            return true
          }
        }
      }
      return false
    }
  },
  created () {
    this.loadCandidateResumeInfo()
    this.loadCandidateConsentInfo()
    this.loadAIInterviewInfo()
    this.viewResume.show = false
  },
  methods: {
    ...mapActions('application', [
      'sendInviteNotification',
      'getExportCandidateProfilePDFTaskStatus',
      'exportCandidateProfilePDF',
      'getExportConsentInfoPDFTaskStatus',
      'exportConsentInfoPDF',
      'exportCandidateProfileWord',
      'createProfileShareLinkCode',
      'getGroupList',
      'saveCandidateGroup',
      'moveCandidateGroup',
      'changeAgencyUploadedCandidateStatus',
      'exportCandidateConsentPDF',
      'getExportCandidateConsentPDFaskStatus',
      'exportApplicationCandidateWord',
      'getExportCandidateWordTaskStatus',
      'getApplicationCandidateResumeInfo',
      'getCandidateConsentInfo',
      'inviteCandidateUploadResume',
      'inviteCandidateAiInterview'
    ]),
    ...mapActions('agreement', ['revokeApplication', 'revokeAllApplication']),
    ...mapActions('talent', ['createTalentCandidateShareLinkCode', 'exportTalentCandidateProfilePdf', 'getExportTalentCandidateProfilePdfTaskStatus', 'getTalentCandidateResumeInfo']),
    ...mapActions('potential', ['getPotentialCandidateResumeInfo']),
    ...mapActions('interview', ['getCoachAIInterviewInfo', 'editInvitation']),
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('ai', ['createAISummary', 'updateAISummary', 'getAISummaryInfoByTaskId']),
    async loadCandidateResumeInfo () {
      if (this.isPotential) {
        const resp = await this.getPotentialCandidateResumeInfo({ potentialId: this.candidateBaseInfo.potential_id })
        if (resp) {
          this.candidateInfo = resp
        }
      } else if (this.isTalentPool) {
        const resp = await this.getTalentCandidateResumeInfo({ candidateId: this.candidateId })
        if (resp) {
          this.candidateInfo = resp
        }
      } else {
        const params = {
          candidate_id: this.candidateId,
          job_id: this.jobId
        }
        const resp = await this.getApplicationCandidateResumeInfo(params)
        if (resp) {
          this.candidateInfo = resp
          if (this.candidateInfo.ai_summary_info) {
            this.aiSummaryDialogInfo.content = getI18nPropertyValue(this.candidateInfo.ai_summary_info, 'ai_summary_cn', 'ai_summary_en')
            this.aiSummaryDialogInfo.isTypingEnabled = false
          }
        }
      }

      const age = moment().diff(this.candidateBaseInfo.birthday, 'year')
      createI18nProperty(this.candidateInfo, 'yearsOld', `${this.i18nPrefix}.label.yearsOld`, { age })

      const baseInfo = this.candidateBaseInfo
      if (['pdf', 'image', 'html', 'docx', 'doc'].includes(baseInfo.resume_file_type)) {
        this.viewResume.hasFile = true
        this.viewResume.type = baseInfo.resume_file_type
        if (baseInfo.resume_file_url.startsWith('http')) {
          this.viewResume.fileURL = baseInfo.resume_file_url
        } else {
          if (process.env.NODE_ENV === 'production') {
            this.viewResume.fileURL = this.$DjangoMediaUrl(baseInfo.resume_file_url)
          } else {
            this.viewResume.fileURL = '/api/media/' + baseInfo.resume_file_url
          }
        }
      }
    },
    async loadAIInterviewInfo () {
      if (this.isPotential) {
        return
      }

      const params = { candidate_id: this.candidateId }
      if (this.jobId) {
        params.job_id = this.jobId
      }
      const resp = await this.getCoachAIInterviewInfo(params)
      if (!resp) {
        return
      }
      this.aiAssessmentInfo = { ...resp, finishedTime: resp.finished_time && moment(resp.finished_time).format('YYYY-MM-DD HH:mm:ss') }
    },
    updateAiAssessmentInfo (info) {
      this.aiAssessmentInfo.is_finished = info.is_finished
      this.aiAssessmentInfo.finished_time = info.finished_time
      this.aiAssessmentInfo.finishedTime = moment(info.finished_time).format('YYYY-MM-DD HH:mm:ss')
    },
    async loadCandidateConsentInfo () {
      if (!this.isTalentPool && this.isPotential) {
        return
      }
      const params = {
        candidate_id: this.candidateId
      }
      if (this.isTalentPool) {
        params.is_talent_pool = true
      } else if (!this.isTalentPool && !this.isPotential) {
        params.job_id = this.jobId
      }
      const resp = await this.getCandidateConsentInfo(params)
      if (resp) {
        _.forEach(resp.consent_info, consent => {
          addI18nProperty(consent, 'i18nContent', 'content_cn', 'content_en')
        })
        this.candidateConsentInfo = resp
      }
    },
    getGroupHistoryVid () {
      const min = _.minBy(this.candidateInfo.group_history, 'id')
      if (min && min.id < 0) {
        return min.id
      } else {
        return -1
      }
    },
    getGroupVid () {
      this.group.vid -= 1
      return this.group.vid
    },
    async acceptAgencyUploadedCandidate (isAccept) {
      if (!isAccept && this.isRejectedAgencyUploadedCandidate) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.changeAgencyUploadedCandidateStatus({
          candidate_id: this.candidateId,
          job_id: this.jobId,
          accepted: isAccept
        })
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.candidateBaseInfo.agency_uploaded_candidate_status = isAccept ? AGENCY_UPLOADED_CANDIDATE_STATUS.ACCEPTED : AGENCY_UPLOADED_CANDIDATE_STATUS.REJECTED
          if (isAccept) {
            this.candidateBaseInfo.is_agency_uploaded_and_hid = false
            await Promise.all([
              this.loadCandidateResumeInfo(),
              new Promise((resolve) => {
                this.$eventBus.$emit('reloadCandidateResumeBaseInfo', resolve)
              })
            ])
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onViewResumeBtnClick () {
      if (!this.viewResume.hasFile) {
        return
      }
      if (this.viewResume.doc) {
        this.handleExportOriginResume()
        return
      }
      this.viewResume.show = !this.viewResume.show
      if (this.viewResume.show) {
        this.viewResume.loading = true
        this.$refs.viewer.render().finally(() => {
          this.viewResume.loading = false
        })
      }
    },
    openInviteApplyJobDialog () {
      this.$refs.inviteApplyJobDialog.openDialog()
    },
    async handleInviteCandidateUploadResume () {
      this.confirmDialogInfo.title = `${this.i18nPrefix}.btn.inviteUploadResume`
      this.confirmDialogInfo.content = `${this.i18nPrefix}.btn.confirmInviteUploadResumeTip`
      this.confirmDialogInfo.show = true
    },
    async handleClickConfirm () {
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      this.confirmDialogInfo.loading = true
      try {
        await this.inviteCandidateUploadResume(params)
        this.$message({
          type: 'success',
          message: this.$t(`${this.i18nPrefix}.message.inviteCandidateUploadSuccess`)
        })
      } finally {
        this.confirmDialogInfo.loading = false
        this.confirmDialogInfo.show = false
      }
    },
    handleClickCancel () {
      this.confirmDialogInfo.show = false
    },
    async inviteFinishProfile () {
      const invitation = this.finishProfileInvitation
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.sendInviteNotification({
          type_id: 0,
          candidate_ids: [this.candidateId],
          job_id: this.jobId
        })
        if (resp) {
          if (resp.success && resp.success.length > 0) {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.success`))
            invitation.invited = true
            invitation.inviteDate = moment().format('YYYY-MM-DD')
          } else if (resp.no_email_count && resp.no_email_count.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.noEmailAccount`))
            invitation.hidden = true // hide the invite button
          } else if (resp.ignore && resp.ignore.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.hasFinished`))
            invitation.hidden = true // hide the invite button
          } else {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.fail`))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleExportOriginResume () {
      const baseInfo = this.candidateBaseInfo
      const candidateName = baseInfo.other_name
      const candidateId = baseInfo.id
      let fileName = `${candidateName}_${candidateId}_${moment().format('YYYYMMDD')}`
      if (!this.isTalentPool) {
        const companyName = baseInfo.company.i18nName
        const jobName = baseInfo.job.i18nName
        fileName = `${companyName}_${jobName}_${fileName}`
      }
      const ext = getFileExtension(baseInfo.resume_path)
      const downloadFileName = ext ? `${fileName}.${ext}` : fileName
      try {
        let result
        if (this.isAgency) {
          result = await agencyApi.getResumeDownloadUrl(this.jobId, candidateId, { file_name: downloadFileName })
        } else if (this.isTalentPool) {
          result = await talentApi.getResumeDownloadUrl(candidateId, { file_name: downloadFileName })
        } else {
          result = await applicationApi.getResumeDownloadUrl(this.jobId, candidateId, { file_name: downloadFileName })
        }
        window.location.href = result.url
      } catch (error) {
        this.$message.error(this.$t('common.message.downloadFailed'))
      }
    },
    handleExportResume () {
      const candidateName = this.candidateBaseInfo.other_name
      const candidateId = this.candidateBaseInfo.id
      const date = moment().format('YYYY-MM-DD')
      let downloadFileName = `${candidateName}_${candidateId}_${date}.pdf`
      if (!this.isTalentPool) {
        const companyName = getI18nPropertyValue(this.candidateBaseInfo.company, 'cn_name', 'name')
        const jobName = this.candidateBaseInfo.job.i18nName
        downloadFileName = `${companyName}_${jobName}_${candidateName}_${candidateId}_${date}.pdf`
      }
      this.exportPDF(true, '0', downloadFileName)
    },
    handleExportReport () {
      this.exportPDF(false, '1')
    },
    handleExportResumeAndReport () {
      this.exportPDF(true, '1')
    },
    handleExportBtnClick (btn) {
      btn.onclick()
    },
    async onExportConsentInfoPDF () {
      const exportConsentStatus = this.exportConsentStatus
      if (exportConsentStatus.loading) {
        return
      }
      exportConsentStatus.loading = true
      exportConsentStatus.finished = false
      exportConsentStatus.task = new AsyncTask(() => this.exportCandidateConsentPDF({
        candidate_id: this.candidateId
      }), (taskId) => this.getExportCandidateConsentPDFaskStatus({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/company/application/export/candidate/consent/?task_id=${taskId}&download=1`)
        exportConsentStatus.loading = false
        exportConsentStatus.finished = false
        exportConsentStatus.task = null
      })
    },
    async onExportWord () {
      const exportWordStatus = this.exportWordStatus
      if (exportWordStatus.loading) {
        return
      }
      exportWordStatus.loading = true
      exportWordStatus.finished = false
      exportWordStatus.task = new AsyncTask(() => this.exportApplicationCandidateWord({
        candidate_id: this.candidateId,
        job_id: this.jobId
      }), (taskId) => this.getExportCandidateWordTaskStatus({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/company/application/export/candidate/word/?task_id=${taskId}&download=1`)
        exportWordStatus.loading = false
        exportWordStatus.finished = false
        exportWordStatus.task = null
      })
    },
    async exportPDF (showBaseInfo, profileType, downloadFileName) {
      const exportPdfStatus = this.exportPdfStatus
      if (exportPdfStatus.loading) {
        return
      }
      exportPdfStatus.loading = true
      exportPdfStatus.finished = false
      if (this.isTalentPool) {
        exportPdfStatus.task = new AsyncTask(() => this.exportTalentCandidateProfilePdf({
          candidate_ids: [this.candidateId],
          show_base_info: showBaseInfo,
          profile_type: profileType
        }), (taskId) => this.getExportTalentCandidateProfilePdfTaskStatus({
          task_id: taskId
        }), (taskId) => {
          let downloadUrl = `/django_rest/company/talent-pool/candidate/export-pdf/?task_id=${taskId}&download=1`
          if (downloadFileName) {
            downloadUrl += `&file_name=${window.encodeURIComponent(downloadFileName)}`
          }
          window.location.href = this.$DjangoBaseUrl(downloadUrl)
          exportPdfStatus.loading = false
          exportPdfStatus.finished = false
          exportPdfStatus.task = null
        })
      } else {
        exportPdfStatus.task = new AsyncTask(() => this.exportCandidateProfilePDF({
          application_id: this.candidateBaseInfo.application_id,
          show_base_info: showBaseInfo,
          profile_type: profileType
        }), (taskId) => this.getExportCandidateProfilePDFTaskStatus({
          task_id: taskId
        }), (taskId) => {
          let downloadUrl = `/django_rest/company/application/export/profile/?task_id=${taskId}&download=1`
          if (downloadFileName) {
            downloadUrl += `&file_name=${window.encodeURIComponent(downloadFileName)}`
          }
          window.location.href = this.$DjangoBaseUrl(downloadUrl)
          exportPdfStatus.loading = false
          exportPdfStatus.finished = false
          exportPdfStatus.task = null
        })
      }
    },
    async onShareBtnClick () {
      if (this.shearLinkLoading) {
        return
      }
      this.shearLinkLoading = true
      let resp = null
      try {
        if (this.isTalentPool) {
          resp = await this.createTalentCandidateShareLinkCode({
            candidate_id: this.candidateId,
            share_type: 'SHARE_CANDIDATE'
          })
        } else {
          resp = await this.createProfileShareLinkCode({
            application_id: this.candidateBaseInfo.application_id,
            share_type: 'SHARE_CANDIDATE'
          })
        }
        if (resp) {
          setTimeout(() => {
            const shareLink = `${window.location.origin}/share/${resp}/`
            window.prompt(this.$t('common.message.copyLink'), shareLink)
          }, 500)
        }
      } finally {
        this.shearLinkLoading = false
      }
    },
    onContactBtnClick () {
      this.$refs.contactDialog.openDialog()
    },
    onEditResumeBtnClick () {
      const back = window.encodeURIComponent(this.$route.fullPath)
      if (this.isTalentPool) {
        const candidateId = this.candidateId
        window.open(`/v-admin/talent-pool/candidate/${candidateId}/edit?back=${back}`)
      } else {
        const jobId = this.jobId
        const candidateApplyJobId = this.candidateBaseInfo.candidate_apply_job_id
        if (this.isAgency) {
          window.open(`/v-agency/application/job/${jobId}/edit/${candidateApplyJobId}?back=${back}`)
        } else {
          window.open(`/v-admin/application/job/${jobId}/edit/${candidateApplyJobId}?back=${back}`)
        }
      }
    },
    openCopyCandidateDialog () {
      this.$refs.copyCandidateDialog.showDialog()
    },
    onEditOpenQuestion () {
      const jobId = this.jobId
      const candidateApplyJobId = this.candidateBaseInfo.candidate_apply_job_id
      const back = window.encodeURIComponent(this.$route.fullPath)
      if (this.isAgency) {
        window.open(`/v-agency/application/job/${jobId}/edit/${candidateApplyJobId}?back=${back}&step=${this.canOpenQuestionScreenType}`)
      } else {
        window.open(`/v-admin/application/job/${jobId}/edit/${candidateApplyJobId}?back=${back}&step=${this.canOpenQuestionScreenType}`)
      }
    },
    onAddCommentBtnClick () {
      this.$eventBus.$emit('showCollapseInfo', 'comment')
    },
    async onSendAiInterviewBtnClick () {
      if (!this.jobId) {
        return
      }
      const params = {
        job_id: this.jobId,
        candidate_id: this.candidateId
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.inviteCandidateAiInterview(params)
        if (resp) {
          if (resp.success) {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.success`))
            this.inviteAIInterview.invited = true
            this.inviteAIInterview.inviteDate = moment().format('YYYY-MM-DD')
          } else if (resp.no_email) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.noEmailAccount`))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleInviteHMRate () {
      this.$refs.inviteHmRatingDialog.showDialog()
    },
    handleSentHmRatingInvite (inviteResult) {
      this.candidateBaseInfo.current_status = addI18nProperty(inviteResult.candidate_status, 'i18nName', 'name_cn', 'name_en')
    },
    handleInviteHMInterview () {
      this.inviteDialog.saveWithEvent = false
      this.inviteDialog.originInvitation = null
      this.inviteDialog.invitation = null
      this.$refs.inviteDialog.showDialog()
    },
    handleSentInterviewInvite (inviteResult) {
      this.candidateBaseInfo.current_status = addI18nProperty(inviteResult.candidate_status, 'i18nName', 'name_cn', 'name_en')
    },
    async handleSaveInterviewInvite (invitation, callback) {
      let success = false
      try {
        const resp = await this.editInvitation({
          ...invitation,
          is_edit: false,
          uid: this.inviteDialog.originInvitation.uuid,
          original_hm_ids: _.map(this.inviteDialog.originInvitation.hire_manager, 'id'),
          has_other_candidate: Object.keys(_.groupBy(this.inviteDialog.originInvitation.candidate, 'id')).length > 1
        })
        if (resp) {
          success = resp.success
          const action = this.$t('common.btn.invite')
          if (success) {
            this.candidateBaseInfo.current_status = addI18nProperty(resp.candidate_status, 'i18nName', 'name_cn', 'name_en')
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
        }
      } finally {
        callback(success)
      }
    },
    async handleSaveAIComments (newAISummaryInfo, callback) {
      this.aiSummaryDialogInfo.content = newAISummaryInfo
      if (isEn()) {
        this.candidateInfo.ai_summary_info.ai_summary_en = this.aiSummaryDialogInfo.content
      } else {
        this.candidateInfo.ai_summary_info.ai_summary_cn = this.aiSummaryDialogInfo.content
      }
      try {
        const params = {
          candidate_id: this.candidateId,
          job_id: this.jobId,
          ai_summary_info: this.aiSummaryDialogInfo.content
        }
        await this.updateAISummary(params)
      } finally {
        callback()
      }
    },
    handleCloseTyping () {
      this.aiSummaryDialogInfo.isTypingEnabled = false
    },
    async onCreateAISummaryBtnClick () {
      if (this.aiSummaryDialogInfo.content) {
        this.aiSummaryDialogInfo.show = true
        this.aiSummaryDialogInfo.isTypingEnabled = false
        return
      }
      const createAISummaryStatus = this.createAISummaryStatus
      if (createAISummaryStatus.loading) {
        return
      }
      this.aiSummaryDialogInfo.show = true
      createAISummaryStatus.finished = false
      this.aiSummaryDialogInfo.loading = true
      this.aiSummaryDialogInfo.isTypingEnabled = false
      createAISummaryStatus.task = new AsyncTask(() => this.createAISummary({
        candidate_id: this.candidateId,
        job_id: this.jobId
      }), (taskId) => this.getAISummaryInfoByTaskId(taskId), (taskId, resp) => {
        let content
        if (resp) {
          content = resp.content
          this.aiSummaryDialogInfo.content = getI18nPropertyValue(content, 'ai_summary_cn', 'ai_summary_en')
          this.aiSummaryDialogInfo.isTypingEnabled = true
        }
        createAISummaryStatus.loading = false
        createAISummaryStatus.finished = false
        createAISummaryStatus.task = null
        this.aiSummaryDialogInfo.loading = false
        if (!this.candidateInfo.ai_summary_info) {
          this.candidateInfo.ai_summary_info = {
            ai_summary_en: '',
            ai_summary_cn: ''
          }
        }
        if (content) {
          this.candidateInfo.ai_summary_info = content
        }
      })
    },
    async onCreateAIResumeMatchBtnClick () {
      this.$message({
        type: 'success',
        message: this.$t(`${this.i18nPrefix}.message.addToGenerateTaskSuccessful`)
      })
      this.$emit('createAIMatch')
    },
    async loadGroupList () {
      if (this.group.loaded) {
        return
      }
      this.group.loading = true
      const params = { job_id: this.jobId }
      if (this.jobId) {
        params.need_talent_pool_group = 1
      } else {
        params.only_talent_pool_group = 1
      }
      const resp = await this.getGroupList(params)
      this.group.loaded = true
      if (resp) {
        this.group.application = _.map(resp.group_list, group => {
          group.selected = this.candidateInfo.group.includes(group.id)
          return group
        })
        this.group.talentPool = resp.talent_pool_group_list || resp.group_list
      }
      this.group.loading = false
      await this.$nextTick()
      if (this.$refs.groupPopover) {
        this.$refs.groupPopover.updatePopper()
      }
    },
    async onGroupBtnClick (btnRef) {
      this.popperElm = btnRef.$el
      if (this.group.showPopover) {
        this.closeGroupListPopover(true)
      } else {
        this.group.showPopover = true
        this.loadGroupList()
      }
    },
    async closeGroupListPopover (inDropdown) {
      if (!this.group.showPopover) {
        return
      }
      await this.saveChangedCandidateGroup()
      this.group.showPopover = false
      if (!inDropdown) {
        this.popperElm = null
      }
    },
    handleGroupClick (group) {
      this.group.hasChanged = true
      if (group.selected) {
        group.count -= 1
        group.selected = false
      } else {
        group.count += 1
        group.selected = true
      }
    },
    async handleAddGroupBtnClick () {
      await this.closeGroupListPopover()
      this.group.unSelectedGroupList = _.filter(this.group.application, group => !group.selected)
      this.$refs.addGroupDialog.openDialog()
    },
    async handleMoveGroupBtnClick () {
      await this.closeGroupListPopover()
      this.group.selectedGroupList = _.filter(this.group.application, 'selected')
      this.group.unSelectedGroupList = _.filter(this.group.application, group => !group.selected)
      this.$refs.moveGroupDialog.openDialog()
    },
    async saveChangedCandidateGroup () {
      if (!this.group.hasChanged || this.group.loading) {
        return
      }
      this.group.loading = true
      try {
        const newBoundGroupIds = []
        const groupList = _.filter(this.group.application, 'selected').map(group => {
          newBoundGroupIds.push(group.id)
          return { id: group.id, text: group.text }
        })
        const needCancelGroupIds = _.filter(this.group.application, group => !group.selected).map(group => group.id)
        const intersectionGroupIds = _.intersection(this.candidateInfo.group, needCancelGroupIds)
        const resp = await this.saveCandidateGroup({
          candidate_ids: [this.candidateId],
          job_id: this.jobId,
          group_list: groupList,
          talent_pool_group: null,
          remove_bound_group: intersectionGroupIds.length > 0
        })
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        } else {
          this.$message.success(this.$t('common.message.saveFail'))
        }
        const groupHistoryList = this.candidateInfo.group_history
        const currentAccount = this.getCurrentAccount()
        let vid = this.getGroupHistoryVid()
        _.forEach(groupList, group => {
          vid -= 1
          groupHistoryList.splice(0, 0, {
            id: vid,
            created_time: new Date(),
            operator: currentAccount.full_name,
            operation: 'A',
            comment: null,
            from_group: null,
            target_group: group
          })
        })
        const boundGroupIds = this.candidateInfo.group
        if (boundGroupIds && boundGroupIds.length > 0) {
          _.forEach(this.group.application, group => {
            if (boundGroupIds.includes(group.id) && !newBoundGroupIds.includes(group.id)) {
              vid -= 1
              groupHistoryList.splice(0, 0, {
                id: vid,
                created_time: new Date(),
                operator: currentAccount.full_name,
                operation: 'R',
                comment: null,
                from_group: null,
                target_group: group
              })
            }
          })
        }
        this.candidateInfo.group = newBoundGroupIds
        this.group.hasChanged = false
      } finally {
        this.group.loading = false
      }
    },
    async saveAddedGroup (groupInfo, callback) {
      const currentGroup = groupInfo.currentGroup
      const talentPoolGroup = groupInfo.talentPoolGroup
      let resp = null
      try {
        resp = await this.saveCandidateGroup({
          candidate_ids: [this.candidateId],
          job_id: this.jobId,
          group_list: currentGroup && [currentGroup],
          talent_pool_group: talentPoolGroup,
          remove_bound_group: false
        })
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        } else {
          this.$message.success(this.$t('common.message.saveFail'))
        }
        const groupHistoryList = this.candidateInfo.group_history
        const currentAccount = this.getCurrentAccount()
        let vid = this.getGroupHistoryVid()
        if (currentGroup) {
          vid -= 1
          groupHistoryList.splice(0, 0, {
            id: vid,
            created_time: new Date(),
            operator: currentAccount.full_name,
            operation: 'A',
            comment: null,
            from_group: null,
            target_group: currentGroup
          })
          if (currentGroup.id) {
            const group = _.find(this.group.application, { id: currentGroup.id })
            group.count += 1
            group.selected = true
          } else {
            this.group.application.push({
              id: resp.group_new_id[currentGroup.text],
              text: currentGroup.text,
              selected: true,
              count: 1
            })
          }
        }
        if (talentPoolGroup && this.isTalentPool) {
          if (talentPoolGroup.id) {
            const group = _.find(this.group.application, { id: talentPoolGroup.id })
            group.count += 1
            group.selected = true
          } else {
            this.group.application.push({
              id: resp.talent_pool_group_new_id[talentPoolGroup.text],
              text: talentPoolGroup.text,
              selected: true,
              count: 1
            })
          }
        }
        if (talentPoolGroup && !talentPoolGroup.id) {
          talentPoolGroup.id = resp.talent_pool_group_new_id[talentPoolGroup.text]
        }
        if (currentGroup) {
          if (this.candidateInfo.group) {
            this.candidateInfo.group.push(currentGroup.id)
          } else {
            this.candidateInfo.group = [currentGroup.id]
          }
        }
      } finally {
        callback(resp)
      }
    },
    async saveMovedGroup (groupInfo, callback) {
      let resp = null
      try {
        resp = await this.moveCandidateGroup({
          candidate_ids: [this.candidateId],
          job_id: this.jobId,
          from_group_id: groupInfo.from,
          to_group: groupInfo.to,
          comment: groupInfo.comment,
          remove_bound_group: false
        })
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        } else {
          this.$message.success(this.$t('common.message.saveFail'))
        }
        const fromGroup = _.find(this.group.application, { id: groupInfo.from })
        fromGroup.selected = false
        fromGroup.count -= 1
        if (groupInfo.to.id) {
          const toGroup = _.find(this.group.application, { id: groupInfo.to.id })
          toGroup.selected = true
          toGroup.count += 1
        } else {
          const toGroup = groupInfo.to
          this.group.application.push({
            id: resp.group_new_id[toGroup.text],
            text: toGroup.text,
            selected: true,
            count: 1
          })
        }
        const groupHistoryList = this.candidateInfo.group_history
        const currentAccount = this.getCurrentAccount()
        const vid = this.getGroupHistoryVid()
        groupHistoryList.splice(0, 0, {
          id: vid,
          created_time: new Date(),
          operator: currentAccount.full_name,
          operation: 'M',
          comment: groupInfo.comment,
          from_group: fromGroup,
          target_group: groupInfo.to
        })
      } finally {
        callback(resp)
      }
    },
    async onRevokeApplicationBtnClick () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.revokeApplication({ candidate_id: this.candidateBaseInfo.id, job_id: this.candidateBaseInfo.job.id })
        if (resp) {
          this.$message.success(this.$t('common.message.actionSuccess', { action: this.$t('common.btn.delete') }))
          const query = this.$route.query
          let url = `/v-admin/job/${query.job_id}/application`
          if (query.back) {
            url = query.back
          }
          this.$router.push(url)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async onRevokeAllApplicationBtnClick () {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.revokeAllApplication({ candidate_id: this.candidateBaseInfo.id })
        if (resp) {
          this.$message.success(this.$t('common.message.actionSuccess', { action: this.$t('common.btn.delete') }))
          const query = this.$route.query
          let url = `/v-admin/job/${query.job_id}/application`
          if (query.back) {
            url = query.back
          }
          this.$router.push(url)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async sendInvitation (invitation) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.sendInviteNotification({
          type_id: invitation.typeId,
          candidate_ids: [this.candidateId],
          job_id: this.jobId
        })
        if (resp) {
          if (resp.success && resp.success.length > 0) {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.success`))
            invitation.invited = true
            invitation.inviteDate = moment().format('YYYY-MM-DD')
          } else if (resp.no_email_count && resp.no_email_count.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.noEmailAccount`))
            invitation.finished = true // set finished=true will hide the invite button
          } else if (resp.ignore && resp.ignore.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.hasFinished`))
            invitation.finished = true
          } else {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.fail`))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onInvitePersonalityBtnClick () {
      const invitation = this.invitationObj.personality
      this.sendInvitation(invitation)
    },
    onInviteProblemSolveBtnClick () {
      const invitation = this.invitationObj.problemSolving
      this.sendInvitation(invitation)
    },
    getExportIconClass (exportStatus) {
      if (exportStatus?.finished) {
        return 'el-icon-download'
      } else if (exportStatus?.task) {
        return ''
      } else {
        return 'el-icon-printer'
      }
    },
    getCreateAISummaryIconClass (createAISummaryStatus) {
      if (createAISummaryStatus?.finished) {
        return 'iconfont icona-ResumeSummary'
      } else if (createAISummaryStatus?.task) {
        return ''
      } else {
        return 'iconfont icona-ResumeSummary'
      }
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.aiSummaryDialogInfo.content = getI18nPropertyValue(this.candidateInfo.ai_summary_info, 'ai_summary_cn', 'ai_summary_en')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.resume-info-container {
  ::v-deep .content-container {
    display: flex;
    justify-content: space-between;
  }

  .resume-info {
    flex: 1;
    overflow: hidden;

    & + .resume-info {
      margin-left: 28px;
    }

    .info-item {
      margin-top: 18px;

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .confirm-send-dialog {
    ::v-deep {
      .el-dialog {
        width: 40%;
      }
    }
  }

  .export-btn {
    color: #0096cc;;
    font-weight: bold;
    border: 1px solid #0096cc;

    &:hover {
      background: #0096cc;
      color: #fff;
    }

    &.is-disabled {
      color: #c0c4cc;
      cursor: not-allowed;
      background-image: none;
      background-color: #fff;
      border-color: #ebeef5;

      &:hover {
        background-color: #fff;
        border-color: #ebeef5;
      }
    }
  }

  .job-application-status {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    color: #606266;
    font-weight: bold;
  }

  .resume-viewer-container {
    flex: 1;
    min-height: 40px;
    position: relative;

    .viewer {
      display: block;
      width: 800px;
      margin: auto;
    }

    .viewer-loading {
      position: absolute;
    }
  }
}

.ai-summary-icon {
  margin-left: 6px;
  margin-right: 0;
}

.group-dropdown {
  max-height: 300px;
  min-width: 200px;
  overflow: auto;

  .dropdown-item {
    padding: 6px 15px;
    line-height: 20px;
    cursor: pointer;

    &:hover {
      background: #e0e0e0;
    }
  }

  .operate-btn {
    color: #0096cc;
  }

  .group-info {
    display: flex;
    position: relative;

    &.selected {
      .dot {
        display: block;
      }
    }

    .dot {
      display: none;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #000;
      position: absolute;
      top: 50%;
      left: 3px;
      transform: translateY(-50%);
    }

    .group-name {
      flex: 1;
      max-width: 300px;
      word-break: keep-all;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .count {
      margin-left: 5px;
      margin-right: 0;
    }
  }
}
</style>

<style lang="scss">
.el-popover.group-popover {
  padding: 5px 0;
}
</style>
